import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { MAIN_FILTERS, type MainFilters } from '@util/firestore/messages';
import { cn } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useConversations } from 'context/ConversationsContext';
import { useTabChats } from 'features/messages/hooks/useTabChats';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, Loader2Icon, MessageCircleOff } from 'lucide-react';
import type { ChatDocument } from 'models/chat';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import MessagePreview from './MessagePreview';

type ConversationListProps = {
  onChatSelected: (chat: ChatDocument, searchTerm?: string) => void;
  onSearchQueryChange?: (query: string) => void;
  isSidebar?: boolean;
  onCloseSidebar?: () => void;
  isModal?: boolean;
};

export default function ConversationList({
  onChatSelected,
  isSidebar,
  onCloseSidebar,
  isModal,
}: ConversationListProps) {
  const { userDoc } = useAuth();
  const isSupportUser = userDoc?.roles?.support || userDoc?.roles?.admin;
  const [isOfferDropdownOpen, setIsOfferDropdownOpen] = useState(false);
  const [isSupportDropdownOpen, setIsSupportDropdownOpen] = useState(false);
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [supportDropdownPosition, setSupportDropdownPosition] = useState({
    top: 0,
    left: 0,
  });
  const [sourceDropdownPosition, setSourceDropdownPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const supportDropdownRef = useRef<HTMLDivElement>(null);
  const supportButtonRef = useRef<HTMLButtonElement>(null);
  const sourceDropdownRef = useRef<HTMLDivElement>(null);
  const sourceButtonRef = useRef<HTMLButtonElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const {
    availableFilters,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter,
  } = useConversations();

  // Calculate dropdown position based on button position
  const calculateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  const calculateSupportDropdownPosition = useCallback(() => {
    if (supportButtonRef.current) {
      const rect = supportButtonRef.current.getBoundingClientRect();
      setSupportDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  const calculateSourceDropdownPosition = useCallback(() => {
    if (sourceButtonRef.current) {
      const rect = sourceButtonRef.current.getBoundingClientRect();
      setSourceDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  // Handle click outside to close dropdown and search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close offer dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOfferDropdownOpen(false);
      }

      // Close support dropdown
      if (
        supportDropdownRef.current &&
        !supportDropdownRef.current.contains(event.target as Node) &&
        supportButtonRef.current &&
        !supportButtonRef.current.contains(event.target as Node)
      ) {
        setIsSupportDropdownOpen(false);
      }

      // Close source dropdown
      if (
        sourceDropdownRef.current &&
        !sourceDropdownRef.current.contains(event.target as Node) &&
        sourceButtonRef.current &&
        !sourceButtonRef.current.contains(event.target as Node)
      ) {
        setIsSourceDropdownOpen(false);
      }

      // Close expanded search on mobile
      if (
        isSearchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSearchExpanded(false);
      }
    };

    if (
      isOfferDropdownOpen ||
      isSupportDropdownOpen ||
      isSourceDropdownOpen ||
      isSearchExpanded
    ) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    isOfferDropdownOpen,
    isSupportDropdownOpen,
    isSourceDropdownOpen,
    isSearchExpanded,
  ]);

  // Recalculate dropdown position on scroll or resize
  useEffect(() => {
    const handlePositionUpdate = () => {
      if (isOfferDropdownOpen) {
        calculateDropdownPosition();
      }
      if (isSupportDropdownOpen) {
        calculateSupportDropdownPosition();
      }
      if (isSourceDropdownOpen) {
        calculateSourceDropdownPosition();
      }
    };

    if (isOfferDropdownOpen || isSupportDropdownOpen || isSourceDropdownOpen) {
      window.addEventListener('scroll', handlePositionUpdate, true);
      window.addEventListener('resize', handlePositionUpdate);
    }

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [
    isOfferDropdownOpen,
    isSupportDropdownOpen,
    isSourceDropdownOpen,
    calculateDropdownPosition,
    calculateSupportDropdownPosition,
    calculateSourceDropdownPosition,
  ]);

  return (
    <div className="flex h-full w-full flex-col gap-y-4 border-r border-zinc-200 bg-white p-4 lg:w-[55rem]">
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-2 p-4">
          <h3 className="text-[2.5rem] font-semibold text-zinc-900 lg:block">
            Messages
          </h3>

          {/* Mobile Search Icon - Only visible on mobile when search is collapsed */}
          <div className="flex flex-1 justify-end lg:hidden">
            {!isSearchExpanded && (
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                className="flex h-12 w-12 items-center justify-center rounded-full transition-colors hover:bg-zinc-100"
                aria-label="Search messages"
              >
                <MagnifyingGlassIcon className="h-8 w-8 text-zinc-600" />
              </button>
            )}
          </div>
        </div>

        {/* Desktop Search Bar - Always visible on desktop */}
        <div className="hidden h-[6rem] w-full items-center justify-between gap-x-4 rounded-[2rem] border border-zinc-200 bg-zinc-50 pl-4 pr-2 focus-within:border-2 focus-within:border-zinc-600 lg:flex">
          <input
            type="text"
            className="h-full w-full border-none bg-transparent text-[1.8rem] text-zinc-700 placeholder:text-zinc-400 focus:ring-0"
            placeholder="Search recent messages"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />

          <div className="mr-4 flex items-center gap-x-2">
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="group flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-zinc-200"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-10 w-10 text-zinc-400 group-hover:text-zinc-500" />
              </button>
            )}

            <MagnifyingGlassIcon className="h-10 w-10 text-zinc-600" />
          </div>
        </div>

        {/* Mobile Expanded Search Bar - Only visible on mobile when expanded */}
        <AnimatePresence>
          {isSearchExpanded && (
            <motion.div
              ref={searchContainerRef}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden lg:hidden"
            >
              <div className="flex h-[6rem] w-full items-center justify-between gap-x-4 rounded-[2rem] border border-zinc-200 bg-zinc-50 pl-4 pr-2 focus-within:border-zinc-400">
                <input
                  type="text"
                  className="h-full w-full border-none bg-transparent text-[1.8rem] text-zinc-700 outline-none placeholder:text-zinc-400"
                  placeholder="Search recent messages"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  autoFocus
                />

                <div className="mr-4 flex items-center gap-x-2">
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="group flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-zinc-200"
                      aria-label="Clear search"
                    >
                      <XMarkIcon className="h-10 w-10 text-zinc-400 group-hover:text-zinc-500" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 flex w-full items-center gap-4 overflow-x-auto overflow-y-visible scroll-smooth py-2">
          {availableFilters.map((filter) => {
            // Special handling for offers filter with dropdown
            if (filter.value === 'offers') {
              const isActive = activeFilter.startsWith('offers');
              const label = activeFilter.startsWith('offers_')
                ? MAIN_FILTERS[activeFilter as keyof typeof MAIN_FILTERS] +
                  ' ' +
                  filter.label
                : filter.label;
              return (
                <div
                  key={filter.value}
                  className={cn(
                    'relative flex h-[4rem] w-fit min-w-[8rem] flex-shrink-0 items-center justify-center gap-4 whitespace-nowrap rounded-full border border-zinc-200 px-6 py-2 text-center font-medium transition-colors',
                    isActive
                      ? 'border-black bg-black text-zinc-100'
                      : 'bg-transparent text-zinc-600 hover:bg-zinc-50'
                  )}
                >
                  <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => {
                      setActiveFilter(filter.value);
                    }}
                  >
                    <span className="flex items-center gap-2">{label}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!isOfferDropdownOpen) {
                        calculateDropdownPosition();
                      }

                      setIsOfferDropdownOpen(!isOfferDropdownOpen);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'h-[10px] w-[1.3px]',
                          isActive ? 'bg-zinc-400' : 'bg-zinc-300'
                        )}
                      />

                      <ChevronDownIcon
                        className={cn(
                          'pointer-events-none h-7 w-7 transition-transform duration-200',
                          isActive ? 'text-zinc-100' : 'text-zinc-600',
                          isOfferDropdownOpen ? 'rotate-180' : ''
                        )}
                      />
                    </div>
                  </button>
                </div>
              );
            }

            // Special handling for source filter with dropdown (support/admin users only)
            if (filter.value === 'source' && isSupportUser) {
              const isActive = activeFilter.startsWith('source');
              const label = activeFilter.startsWith('source_')
                ? MAIN_FILTERS[activeFilter as keyof typeof MAIN_FILTERS] +
                  ' ' +
                  filter.label
                : filter.label;
              return (
                <div
                  key={filter.value}
                  className={cn(
                    'relative flex h-[4rem] w-fit min-w-[8rem] flex-shrink-0 items-center justify-center gap-4 whitespace-nowrap rounded-full border border-zinc-200 px-6 py-2 text-center font-medium transition-colors',
                    isActive
                      ? 'border-black bg-black text-zinc-100'
                      : 'bg-transparent text-zinc-600 hover:bg-zinc-50'
                  )}
                >
                  <button
                    ref={sourceButtonRef}
                    type="button"
                    onClick={() => {
                      setActiveFilter(filter.value);
                    }}
                  >
                    <span className="flex items-center gap-2">{label}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!isSourceDropdownOpen) {
                        calculateSourceDropdownPosition();
                      }

                      setIsSourceDropdownOpen(!isSourceDropdownOpen);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'h-[10px] w-[1.3px]',
                          isActive ? 'bg-zinc-400' : 'bg-zinc-300'
                        )}
                      />

                      <ChevronDownIcon
                        className={cn(
                          'pointer-events-none h-7 w-7 transition-transform duration-200',
                          isActive ? 'text-zinc-100' : 'text-zinc-600',
                          isSourceDropdownOpen ? 'rotate-180' : ''
                        )}
                      />
                    </div>
                  </button>
                </div>
              );
            }

            // Special handling for support filter with dropdown (support/admin users only)
            if (filter.value === 'support' && isSupportUser) {
              const isActive = activeFilter.startsWith('support');
              const label = activeFilter.startsWith('support_')
                ? MAIN_FILTERS[activeFilter as keyof typeof MAIN_FILTERS] +
                  ' ' +
                  filter.label
                : filter.label;
              return (
                <div
                  key={filter.value}
                  className={cn(
                    'relative flex h-[4rem] w-fit min-w-[8rem] flex-shrink-0 items-center justify-center gap-4 whitespace-nowrap rounded-full border border-zinc-200 px-6 py-2 text-center font-medium transition-colors',
                    isActive
                      ? 'border-black bg-black text-zinc-100'
                      : 'bg-transparent text-zinc-600 hover:bg-zinc-50'
                  )}
                >
                  <button
                    ref={supportButtonRef}
                    type="button"
                    onClick={() => {
                      setActiveFilter(filter.value);
                    }}
                  >
                    <span className="flex items-center gap-2">{label}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!isSupportDropdownOpen) {
                        calculateSupportDropdownPosition();
                      }

                      setIsSupportDropdownOpen(!isSupportDropdownOpen);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'h-[10px] w-[1.3px]',
                          isActive ? 'bg-zinc-400' : 'bg-zinc-300'
                        )}
                      />

                      <ChevronDownIcon
                        className={cn(
                          'pointer-events-none h-7 w-7 transition-transform duration-200',
                          isActive ? 'text-zinc-100' : 'text-zinc-600',
                          isSupportDropdownOpen ? 'rotate-180' : ''
                        )}
                      />
                    </div>
                  </button>
                </div>
              );
            }

            if (
              !filter.value.startsWith('offers_') &&
              !filter.value.startsWith('support_') &&
              !filter.value.startsWith('source_') &&
              filter.value !== 'source'
            ) {
              // Regular filter buttons
              const isActive = activeFilter === filter.value;
              return (
                <button
                  type="button"
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value)}
                  className={cn(
                    'flex h-[4rem] w-fit min-w-[8rem] flex-shrink-0 items-center justify-center gap-2 rounded-full border border-zinc-200 px-6 py-2 text-center font-medium transition-colors',
                    isActive
                      ? 'border-black bg-black text-zinc-100'
                      : 'bg-transparent text-zinc-600 hover:bg-zinc-50'
                  )}
                >
                  <div className="flex items-center gap-2">{filter.label}</div>
                </button>
              );
            }
            return null;
          })}
        </div>
      </div>
      {Object.keys(MAIN_FILTERS).map((filter) => {
        if (filter !== activeFilter) return null;
        return (
          <Conversations
            key={filter}
            tab={filter as MainFilters}
            onClick={onChatSelected}
          />
        );
      })}
      {/* Dropdown Menu - Fixed positioned to escape scroll container */}
      {isOfferDropdownOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-[18rem] rounded-2xl border border-zinc-200 bg-white shadow-lg"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className="py-2">
            {Object.keys(MAIN_FILTERS)
              .filter((key) => key.startsWith('offers_'))
              .map((state) => (
                <button
                  key={state}
                  type="button"
                  onClick={() => {
                    setActiveFilter(state as MainFilters);
                    setIsOfferDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full whitespace-nowrap px-4 py-3 text-left text-[1.4rem] transition-colors hover:bg-zinc-50',
                    activeFilter === state
                      ? 'bg-zinc-100 font-medium text-zinc-900'
                      : 'text-zinc-600'
                  )}
                >
                  {MAIN_FILTERS[state as keyof typeof MAIN_FILTERS]}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Support State Dropdown Menu */}
      {isSupportDropdownOpen && (
        <div
          ref={supportDropdownRef}
          className="fixed z-[9999] w-56 rounded-2xl border border-zinc-200 bg-white shadow-lg"
          style={{
            top: `${supportDropdownPosition.top}px`,
            left: `${supportDropdownPosition.left}px`,
          }}
        >
          <div className="py-2">
            {Object.keys(MAIN_FILTERS)
              .filter((key) => key.startsWith('support_'))
              .map((state) => (
                <button
                  key={state}
                  type="button"
                  onClick={() => {
                    setActiveFilter(state as MainFilters);
                    setIsSupportDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left text-[1.4rem] transition-colors hover:bg-zinc-50',
                    activeFilter === state
                      ? 'bg-zinc-100 font-medium text-zinc-900'
                      : 'text-zinc-600'
                  )}
                >
                  {MAIN_FILTERS[state as keyof typeof MAIN_FILTERS]}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Source Dropdown Menu */}
      {isSourceDropdownOpen && (
        <div
          ref={sourceDropdownRef}
          className="fixed z-[9999] w-56 rounded-2xl border border-zinc-200 bg-white shadow-lg"
          style={{
            top: `${sourceDropdownPosition.top}px`,
            left: `${sourceDropdownPosition.left}px`,
          }}
        >
          <div className="py-2">
            {Object.keys(MAIN_FILTERS)
              .filter((key) => key.startsWith('source_'))
              .map((source) => (
                <button
                  key={source}
                  type="button"
                  onClick={() => {
                    setActiveFilter(source as MainFilters);
                    setIsSourceDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left text-[1.4rem] transition-colors hover:bg-zinc-50',
                    activeFilter === source
                      ? 'bg-zinc-100 font-medium text-zinc-900'
                      : 'text-zinc-600'
                  )}
                >
                  {MAIN_FILTERS[source as keyof typeof MAIN_FILTERS]}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* View All Messages Button - Only visible in sidebar mode */}
      {isSidebar && (
        <>
          <div className="absolute bottom-0 z-20 w-full">
            <div className="flex w-full flex-col gap-6 py-12 pr-8">
              <Link
                href="/dashboard/messages"
                className="flex h-[6rem] w-full items-center justify-center rounded-[2rem] bg-brand-secondary text-center text-[2.1rem] font-semibold text-white transition-colors duration-75 ease-linear hover:bg-brand-secondary-darker"
                onClick={() => {
                  onCloseSidebar?.();
                }}
              >
                View all messages
              </Link>
            </div>
          </div>

          <div className="absolute bottom-0 z-10 h-[30rem] w-full bg-gradient-to-t from-white to-transparent" />
        </>
      )}
    </div>
  );
}

function Conversations({
  tab,
  onClick,
}: {
  tab: MainFilters;
  onClick: (chat: ChatDocument) => void;
}) {
  const { chats, loadMore, hasMore, isLoading } = useTabChats(tab);

  return (
    <div className="h-full">
      <Virtuoso
        style={{ height: '100%' }}
        data={chats}
        itemContent={(_, chat) => (
          <MessagePreview
            key={chat.id}
            chat={chat}
            onClick={() => onClick(chat)}
          />
        )}
        endReached={hasMore ? loadMore : undefined}
        components={{
          Footer: () =>
            !hasMore && !isLoading ? (
              <div className="flex w-full justify-center py-4">
                <div className="flex w-fit items-center gap-4 rounded-[2rem] border-2 border-dashed border-zinc-200 bg-white/60 px-8 py-4 backdrop-blur-3xl">
                  <MessageCircleOff className="h-6 w-6 text-zinc-500" />

                  <span className="text-[1.5rem] text-zinc-500">
                    {chats.length === 0
                      ? 'No conversations found'
                      : "You've reached the end of the list."}
                  </span>
                </div>
              </div>
            ) : null,
        }}
      />

      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              duration: 0.4,
            }}
            className="relative flex w-full justify-center"
          >
            <div className="absolute bottom-4 flex items-center gap-4 rounded-[2rem] border border-zinc-200 bg-white/60 px-8 py-4 backdrop-blur-3xl">
              <Loader2Icon className="h-6 w-6 animate-spin text-zinc-500" />
              <span className="text-[1.5rem] text-zinc-500">
                Loading more chats...
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
